import {useMemo} from 'react';

import {panelIsActive, searchRegexMatchesPanel} from '../../../util/panelbank';
import {LayedOutPanelWithRef} from '../../../util/panelTypes';
import {useKeyTypes} from '../../../util/runhelpers';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {SectionWithRef} from '../types';

export const useSectionPanels = (
  sectionWithRef: SectionWithRef,
  searchRegex: RegExp | null,
  isSectionNameSearchMatch: boolean
) => {
  const {isSingleRun} = useSingleRunModeContext();
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();

  const keyTypes = useKeyTypes(historyKeyInfo.keys);

  // activePanels are the panels that may get rendered.
  // NOTE: this includes panels that match and don't match the panel search regex.
  // we want to keep them in the same array so we can maintain the panel order
  const activePanels = useMemo(() => {
    return sectionWithRef.panels.filter((panel: LayedOutPanelWithRef) => {
      const isPanelActive = panelIsActive({
        section: sectionWithRef,
        panel,
        isSingleRun,
        historyKeyInfo,
        keyTypes,
      });

      return isPanelActive;
    });
  }, [historyKeyInfo, isSingleRun, keyTypes, sectionWithRef]);

  const filteredPanels = useMemo(() => {
    return getSectionFilteredPanels({
      activePanels,
      searchRegex,
      isSectionNameSearchMatch,
    });
  }, [activePanels, isSectionNameSearchMatch, searchRegex]);

  return useMemo(() => {
    return {
      activePanels,
      filteredPanels,
    };
  }, [activePanels, filteredPanels]);
};

export const getSectionFilteredPanels = ({
  activePanels,
  searchRegex,
  isSectionNameSearchMatch,
}: {
  activePanels: LayedOutPanelWithRef[];
  searchRegex: RegExp | null;
  isSectionNameSearchMatch: boolean;
}) => {
  if (!searchRegex) {
    return activePanels;
  }

  if (isSectionNameSearchMatch) {
    // If the section name matches the search, return all active panels
    // This is to avoid showing empty sections when searching by section name
    return activePanels;
  }

  return activePanels.filter(panel => {
    return searchRegexMatchesPanel(searchRegex, panel);
  });
};
